import React, { useState, useEffect, useCallback } from "react";
import {
  getHeroBannerById,
  addHeroBanner,
  updateHeroBanner,
} from "../../../services/heroBannersService";
import { getHeroBannerButtonsList } from "../../../services/heroBannerButtonsService";
import { syncHeroBannerButtons } from "../../../services/heroBannerSyncService";
import { validateHeroBanner } from "../../../utils/validation";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { handleErrors } from "../../../utils/errorHandler";
import ComponentHeader from "../../Common/OtherElements/ComponentHeader";
import ImageUpload from "../../Common/ImageUpload/ImageUpload";
import { useMediaLibraryIntegration } from "../../MediaLibrary/MediaSourcePicker";
import HeroBannerButtonsSection from "./HeroBannerButtonsSection";
import {
  BANNER_TYPE_OPTIONS,
} from "./heroBannerConstants";
import {
  normalizeBannerToForm,
  buildHeroBannerFormData,
  extractListResult,
  filterButtonsByBannerId,
  extractMutationId,
} from "./heroBannerUtils";
const INITIAL_FORM_STATE = {
  bannerName: "",
  bannerType: "Image",
  bannerTitle: "",
  bannerDescription: "",
  displayOrder: "",
  isActive: true,
  desktopImageFile: null,
  mobileImageFile: null,
  desktopImagePreview: "",
  mobileImagePreview: "",
  desktopImageUrl: "",
  mobileImageUrl: "",
  buttons: [],
};

const INITIAL_ERRORS_STATE = {
  bannerName: "",
  bannerType: "",
  bannerTitle: "",
  displayOrder: "",
  desktopImage: "",
  buttons: {},
  buttonsRoot: "",
};

export const AddHeroBanner = ({
  editMode = false,
  initialData = {},
  onSuccess,
  setSelectedBanner,
  setEditMode,
}) => {
  const [formData, setFormData] = useState(INITIAL_FORM_STATE);
  const [errors, setErrors] = useState(INITIAL_ERRORS_STATE);
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [deletedButtonIds, setDeletedButtonIds] = useState([]);
  const [loading, setLoading] = useState(false);
  // Per-field Media Library selection, keyed by file field (this session only).
  const [selectedMedia, setSelectedMedia] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      const bannerId = initialData?.id ?? initialData?.Id;
      if (editMode && bannerId) {
        setLoading(true);
        try {
          const [banner, buttonsRes] = await Promise.all([
            getHeroBannerById(bannerId),
            getHeroBannerButtonsList(),
          ]);
          const allButtons = extractListResult(buttonsRes);
          const bannerButtons = filterButtonsByBannerId(allButtons, bannerId);
          setFormData(normalizeBannerToForm(banner, bannerButtons));
          setDeletedButtonIds([]);
          setSelectedMedia({});
        } catch (error) {
          handleErrors(error);
        } finally {
          setLoading(false);
        }
      } else {
        setFormData(INITIAL_FORM_STATE);
        setDeletedButtonIds([]);
        setSelectedMedia({});
      }
    };
    fetchData();
  }, [editMode, initialData]);

  const handleInputChange = ({ target: { name, value, type, checked } }) => {
    const nextValue = type === "checkbox" ? checked : value;
    setFormData((prev) => ({ ...prev, [name]: nextValue }));
    setErrors((prev) => ({ ...prev, [name]: "", buttonsRoot: "" }));
  };

  const handleDisplayOrderChange = (e) => {
    const { value } = e.target;
    if (value === "" || /^[1-9]\d*$/.test(value)) {
      setFormData((prev) => ({ ...prev, displayOrder: value }));
      setErrors((prev) => ({ ...prev, displayOrder: "" }));
    }
  };

  const handleDisplayOrderKeyDown = (e) => {
    if (["e", "E", "+", "-", "."].includes(e.key)) {
      e.preventDefault();
    }
  };

  const handleMediaSelect = (media, fileField, previewField, urlField) => {
    const url = media?.mediaUrl || media?.thumbnailUrl || "";
    if (!url) return;
    setFormData((prev) => ({
      ...prev,
      [fileField]: null,
      [previewField]: url,
      [urlField]: url,
    }));
    setSelectedMedia((prev) => ({ ...prev, [fileField]: media }));
    setErrors((prev) => ({ ...prev, desktopImage: "" }));
  };

  // Remove only the Media Library selection for a given field.
  const handleMediaRemove = (fileField, previewField, urlField, errorKey) => {
    setFormData((prev) => ({
      ...prev,
      [fileField]: null,
      [previewField]: "",
      [urlField]: "",
    }));
    setSelectedMedia((prev) => ({ ...prev, [fileField]: null }));
    if (errorKey) {
      setErrors((prev) => ({ ...prev, [errorKey]: "" }));
    }
  };

  const desktopImageLibrary = useMediaLibraryIntegration({
    media: selectedMedia.desktopImageFile,
    imageUrl: formData.desktopImageUrl,
    defaultKeyword: formData.bannerTitle,
    onSelect: (media) =>
      handleMediaSelect(
        media,
        "desktopImageFile",
        "desktopImagePreview",
        "desktopImageUrl"
      ),
    onRemove: () =>
      handleMediaRemove(
        "desktopImageFile",
        "desktopImagePreview",
        "desktopImageUrl",
        "desktopImage"
      ),
  });

  const mobileImageLibrary = useMediaLibraryIntegration({
    media: selectedMedia.mobileImageFile,
    imageUrl: formData.mobileImageUrl,
    defaultKeyword: formData.bannerTitle,
    onSelect: (media) =>
      handleMediaSelect(
        media,
        "mobileImageFile",
        "mobileImagePreview",
        "mobileImageUrl"
      ),
    onRemove: () =>
      handleMediaRemove(
        "mobileImageFile",
        "mobileImagePreview",
        "mobileImageUrl"
      ),
  });

  const updateButtonAt = (index, patch) => {
    setFormData((prev) => {
      const nextButtons = [...prev.buttons];
      nextButtons[index] = { ...nextButtons[index], ...patch };
      return { ...prev, buttons: nextButtons };
    });
    setErrors((prev) => ({ ...prev, buttons: {}, buttonsRoot: "" }));
  };

  const handleAddButton = (button) => {
    setFormData((prev) => ({
      ...prev,
      buttons: [...prev.buttons, button],
    }));
  };

  const handleRemoveButton = (index) => {
    const btn = formData.buttons[index];
    if (btn?.id) {
      setDeletedButtonIds((prev) => [...prev, btn.id]);
    }
    setFormData((prev) => ({
      ...prev,
      buttons: prev.buttons
        .filter((_, i) => i !== index)
        .map((b, i) => ({ ...b, displayOrder: i + 1 })),
    }));
  };

  const resetForm = () => {
    setFormData(INITIAL_FORM_STATE);
    setErrors(INITIAL_ERRORS_STATE);
    setDeletedButtonIds([]);
    setSelectedMedia({});
  };

  const handleCancelEdit = () => {
    resetForm();
    setSelectedBanner(null);
    setEditMode(false);
  };

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      const { valid, errors: validationErrors } = validateHeroBanner(
        formData,
        editMode
      );
      setErrors({
        bannerName: validationErrors.bannerName || "",
        bannerType: validationErrors.bannerType || "",
        bannerTitle: validationErrors.bannerTitle || "",
        displayOrder: validationErrors.displayOrder || "",
        desktopImage: validationErrors.desktopImage || "",
        buttons: validationErrors.buttons || {},
        buttonsRoot: validationErrors.buttons?._root || "",
      });

      if (!valid) {
        toast.error("Please fix validation errors before saving");
        return;
      }

      const bannerId = editMode ? initialData?.id ?? initialData?.Id : undefined;

      try {
        setIsButtonDisabled(true);
        const payload = buildHeroBannerFormData(formData, editMode, bannerId);
        const action = editMode ? updateHeroBanner : addHeroBanner;
        const response = await action(payload);

        if (response?.data?.isSuccess === false) {
          toast.error(
            response.data.message || "Request failed. Please try again."
          );
          return;
        }

        const savedBannerId =
          editMode ? bannerId : extractMutationId(response);

        if (!savedBannerId) {
          toast.error("Banner saved but ID was not returned from API");
          return;
        }

        await syncHeroBannerButtons(
          savedBannerId,
          formData.buttons,
          deletedButtonIds
        );

        toast.success(
          `Hero banner ${editMode ? "updated" : "added"} successfully!`
        );
        resetForm();
        if (onSuccess) onSuccess();
        setEditMode(false);
        setSelectedBanner(null);
      } catch (error) {
        handleErrors(error);
      } finally {
        setIsButtonDisabled(false);
      }
    },
    [
      formData,
      editMode,
      initialData,
      deletedButtonIds,
      onSuccess,
      setEditMode,
      setSelectedBanner,
    ]
  );

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-secondary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <>
      <ComponentHeader title="Hero Banners" />

      <div className="row">
        <div className="col-xxl-12">
          <div className="card mt-xxl-n5">
            <div className="card-header">
              <h5 className="mb-sm-1 mt-sm-1">
                {editMode ? "Update Hero Banner" : "Add Hero Banner"}
              </h5>
            </div>
            <div className="card-body p-4">
              <form onSubmit={handleSubmit} method="POST">
                <div className="row">
                  <div className="col-lg-4 col-md-6">
                    <div className="mb-3">
                      <label htmlFor="bannerName" className="form-label">
                        Banner Name <span className="required-field">*</span>
                      </label>
                      <input
                        type="text"
                        name="bannerName"
                        id="bannerName"
                        className={`form-control ${
                          errors.bannerName ? "is-invalid" : ""
                        }`}
                        placeholder="Enter banner name"
                        value={formData.bannerName}
                        onChange={handleInputChange}
                      />
                      {errors.bannerName && (
                        <div className="invalid-feedback">
                          {errors.bannerName}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="col-lg-4 col-md-6">
                    <div className="mb-3">
                      <label htmlFor="bannerType" className="form-label">
                        Banner Type <span className="required-field">*</span>
                      </label>
                      <select
                        name="bannerType"
                        id="bannerType"
                        className={`form-select ${
                          errors.bannerType ? "is-invalid" : ""
                        }`}
                        value={formData.bannerType}
                        onChange={handleInputChange}
                      >
                        {BANNER_TYPE_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                      {errors.bannerType && (
                        <div className="invalid-feedback d-block">
                          {errors.bannerType}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="col-lg-4 col-md-6">
                    <div className="mb-3">
                      <label htmlFor="displayOrder" className="form-label">
                        Display Order <span className="required-field">*</span>
                      </label>
                      <input
                        type="number"
                        name="displayOrder"
                        id="displayOrder"
                        min="1"
                        step="1"
                        className={`form-control ${
                          errors.displayOrder ? "is-invalid" : ""
                        }`}
                        placeholder="Enter display order"
                        value={formData.displayOrder}
                        onChange={handleDisplayOrderChange}
                        onKeyDown={handleDisplayOrderKeyDown}
                      />
                      {errors.displayOrder && (
                        <div className="invalid-feedback">
                          {errors.displayOrder}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="col-lg-8 col-md-6">
                    <div className="mb-3">
                      <label htmlFor="bannerTitle" className="form-label">
                        Banner Title <span className="required-field">*</span>
                      </label>
                      <input
                        type="text"
                        name="bannerTitle"
                        id="bannerTitle"
                        className={`form-control ${
                          errors.bannerTitle ? "is-invalid" : ""
                        }`}
                        placeholder="Enter banner title"
                        value={formData.bannerTitle}
                        onChange={handleInputChange}
                      />
                      {errors.bannerTitle && (
                        <div className="invalid-feedback">
                          {errors.bannerTitle}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="col-lg-4 col-md-6">
                    <div className="mb-3">
                      <label className="form-label d-block">Status</label>
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          name="isActive"
                          id="isActive"
                          checked={formData.isActive}
                          onChange={handleInputChange}
                        />
                        <label className="form-check-label" htmlFor="isActive">
                          {formData.isActive ? "Active" : "Inactive"}
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className="col-12">
                    <div className="mb-3">
                      <label htmlFor="bannerDescription" className="form-label">
                        Banner Description
                      </label>
                      <textarea
                        name="bannerDescription"
                        id="bannerDescription"
                        rows="3"
                        className="form-control"
                        placeholder="Enter banner description"
                        value={formData.bannerDescription}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>

                  <div className="col-lg-6">
                    <div className="mb-3">
                      <ImageUpload
                        label="Desktop Image"
                        required={!editMode}
                        previewUrl={formData.desktopImagePreview}
                        error={errors.desktopImage}
                        {...desktopImageLibrary.uploadEnhancements}
                      />
                      {desktopImageLibrary.modalSlot}
                      {editMode && (
                        <small className="text-muted d-block mt-1">
                          Leave empty to keep current image
                        </small>
                      )}
                    </div>
                  </div>

                  <div className="col-lg-6">
                    <div className="mb-3">
                      <ImageUpload
                        label="Mobile Image"
                        previewUrl={formData.mobileImagePreview}
                        {...mobileImageLibrary.uploadEnhancements}
                      />
                      {mobileImageLibrary.modalSlot}
                      <small className="text-muted d-block mt-1">
                        Optional mobile-optimized image
                      </small>
                    </div>
                  </div>

                  <div className="col-12">
                    <div className="mb-3">
                      <HeroBannerButtonsSection
                        buttons={formData.buttons}
                        onChange={updateButtonAt}
                        onAdd={handleAddButton}
                        onRemove={handleRemoveButton}
                        buttonErrors={errors.buttons}
                        rootError={errors.buttonsRoot}
                      />
                    </div>
                  </div>

                  <div className="col-12">
                    <button
                      type="submit"
                      className="btn btn-secondary pt-1 pb-1 p-3"
                      disabled={isButtonDisabled}
                    >
                      {isButtonDisabled
                        ? editMode
                          ? "Updating"
                          : "Saving"
                        : editMode
                        ? "Update"
                        : "Save"}
                    </button>
                    {editMode && (
                      <button
                        type="button"
                        onClick={handleCancelEdit}
                        className="btn btn-danger ms-1 pt-1 pb-1 p-3"
                      >
                        Cancel Edit
                      </button>
                    )}
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
